/**
 * Sidebar v7 — Block C1 (Jun 21): registry-driven CASHIER nav + admin industry injection.
 *
 * Changes vs v6:
 *   - CASHIER nav is now driven entirely by the surface registry (getSurfaces),
 *     the SAME source the bottom nav uses — fixing the drift where the desktop
 *     cashier sidebar showed a hardcoded list and never picked up industry
 *     surfaces (e.g. a salon cashier on a laptop never saw "Appointments").
 *     The hardcoded CASHIER_OPERATIONS / CASHIER_ACCOUNT arrays are gone;
 *     cashier groups are composed from registry surfaces, grouped by
 *     surface.section ("operations" default, "account" for chat/profile).
 *   - ADMIN nav keeps its declarative chrome (Workspace/Operations/Account/
 *     Group) but now INJECTS live industry/module surfaces into Workspace via
 *     getSurfaces({ app: "admin" }) — so a salon admin gains "Appointments"
 *     too. IA: the real schedule surface is primary; the generic Orders item
 *     reads "Sales" for salon (industries.ts ordersPageTitle), so no
 *     double-"Appointments".
 *   - Shared label resolver handles the nested schedule term
 *     (terms.schedule.title) that the flat t(termKey) can't reach.
 *
 * v6 carried forward:
 *   - Enterprise detection from persisted user.company.subscription.plan first,
 *     business-count only once loaded, last-known held while loading.
 *   - Industry-aware labels via useT() for declarative admin items.
 *   - "Add business" affordance; Day-close in Operations.
 */
import React, { useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Scale,
  ListTree,
  Receipt,
  Package,
  ClipboardList,
  AlertTriangle,
  Settings,
  History,
  LogOut,
  ShoppingCart,
  BadgeJapaneseYen,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
  FileBarChart,
  Handshake,
  UserCog,
  Boxes,
  ShieldCheck,
  LayersIcon,
  ArrowRightLeft,
  Plus,
  Award,
  ClipboardCheck,
  Building2,
  HandCoins,
  PackagePlus,
  PackageSearch,
  MapPinned,
  BarChart3,
  ShoppingBag,
  Navigation,
  Factory,
  CalendarClock,
  Undo2,
  Tag,
  Sparkles,
  Users,
  CalendarDays,
  Banknote,
  UserRound,
  HeartHandshake,
  ChevronDown,
  Lock,
  FileText,
  Truck,
  Tags,
  Radar,
  IdCard,
  BookOpen,
  Landmark,
  Warehouse,
  Percent,
  Layers,
  Pill,
  Wrench,
  Weight,
  CalendarRange,
  FileSignature,
  Timer,
  Contact,
  BookLock,
  ShieldAlert,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useAuthStore, {
  isAnyAdmin,
  isCashier,
  type UserRole,
} from "@/stores/authStore";
import { useMyBusinesses } from "@/hooks/useBusinesses";
import { useT } from "@/hooks/useIndustry";
import { useChatUnread } from "@/hooks/useChatUnread";
import {
  getSurfaces,
  type Role,
  type SurfaceDef,
} from "@/constants/industry-surfaces";
import type { IndustryId, StringTermKey } from "@/constants/industries";
import type { BusinessFeatureKey } from "@/types/business.types";
import { useEffectiveRole } from "@/components/auth/RouteGuards";
import { usePermissions } from "@/hooks/usePermissions";
import { useTeamChatAvailable } from "@/hooks/useTeamChatAvailable";
import Logo from "@/assets/logo.svg";
import AddBusinessDialog from "../admin/AddBusinessDialog";
import { atLimit, hasModule, useEntitlements } from "@/hooks/useEntitlements";
import useBusinessStore from "@/stores/businessStore";

const TENANT_LEVEL_ROLES: UserRole[] = ["tenant_owner", "super_admin"];

export interface NavItem {
  icon: LucideIcon;
  label: string;
  path: string;
  visibleTo?: UserRole[];
  /** Shown only when the person holds this permission (any of a list). */
  perm?: string | string[];
  /** Hidden when the person holds this permission (e.g. self-service for non-HR staff). */
  hideIfPerm?: string;
  /** Shown only when the open business uses this part of Eaze (Settings → What this business does). */
  uses?: BusinessFeatureKey | BusinessFeatureKey[];
  /** Shown only for these industries (default: every industry). */
  only?: IndustryId[];
  /** Hidden for these industries. */
  hideFor?: IndustryId[];
  /**
   * When set, the displayed label is taken from the active industry's
   * terminology (useT) instead of the static `label`. Surface-derived items
   * bake their resolved label into `label` and leave this unset.
   */
  termKey?: StringTermKey;
  /** Live count badge resolved at render time (team-chat unread). */
  badgeKey?: "chatUnread";
  /**
   * Plan module this needs (purchasing, hr, payroll, crm …). Not in the
   * plan → owners see it locked (it opens Settings → Subscription); everyone
   * else doesn't see it. The server refuses it either way.
   */
  feature?: string;
  /** Set at render time: shown locked (not in the plan). */
  locked?: boolean;
  /**
   * Shop-floor tools (shelf labels, promotions, loyalty): hidden for
   * businesses that sell in bulk to other businesses (wholesale,
   * manufacturing, pharma wholesale) and for builders and service firms.
   */
  shop?: boolean;
  /** Batch / lot tracking (batches, recalls): only where lots matter. */
  lots?: boolean;
  /** Dispensing to patients: retail pharmacies only (not wholesale or OTC sellers). */
  dispensing?: boolean;
  /** One business's own work (day close…): not in All businesses mode. */
  businessOnly?: boolean;
  /** Leaves the open business for All businesses (group mode). */
  allBusinesses?: boolean;
}

// ── Admin navigation: grouped, industry-scoped, permission-driven ────
// An item shows when industry ∩ permission allow it (plan entitlements join
// with M4). Industry/module surfaces from the registry are added to "Sell".
const SHELF_RETAIL: IndustryId[] = ["retail", "pharmacy", "hardware", "wholesale", "other"];
/** Industries that track lots (batches, recalls). */
const LOT_INDUSTRIES: IndustryId[] = ["pharmacy", "wholesale", "manufacturing", "restaurant", "other"];
/** Industries with a shop floor (shelf labels, promotions, loyalty). */
const SHOP_INDUSTRIES: IndustryId[] = ["retail", "pharmacy", "hardware", "restaurant", "salon", "gym", "other"];
/** Segments that sell in bulk to businesses, whatever the industry. */
const BULK_SEGMENTS = new Set(["pharma_wholesale", "building_materials_trade", "fmcg", "general_wholesale"]);

/**
 * Menu paths that come from the industry registry (not declared above) and
 * the business switch they follow — any one of a list is enough.
 */
const PATH_USES: Record<string, BusinessFeatureKey | BusinessFeatureKey[]> = {
  "/projects": ["projects", "unit_sales"],
  "/collections": ["projects", "unit_sales"],
  "/cashier/collect": ["projects", "unit_sales"],
  "/cashier/schedule": "bookings",
  "/schedule": "bookings",
  "/cashier/checkin": "memberships",
  "/classes": "memberships",
  "/cashier/stock-take": "keeps_stock",
  "/cashier/inventory": "keeps_stock",
  "/stock-transfers": "keeps_stock",
};

const ADMIN_HOME: NavItem[] = [
  { icon: LayoutDashboard, label: "Dashboard", path: "/dashboard", perm: "report.dashboard.read" },
];

const ADMIN_SELL: NavItem[] = [
  { icon: ClipboardList, label: "Orders", path: "/orders", termKey: "ordersPageTitle", perm: "order.read" },
  { icon: Undo2, label: "Returns", path: "/returns", perm: "order.read", uses: "sells_goods" },
  { icon: FileText, label: "Quotations", path: "/quotations", perm: "sales-order.read", feature: "wholesale", uses: "sells_goods", hideFor: ["gym", "salon"] },
  { icon: Truck, label: "Sales orders", path: "/sales-orders", perm: "sales-order.read", feature: "wholesale", uses: "sells_goods", hideFor: ["gym", "salon", "services"] },
  { icon: Tags, label: "Price lists", path: "/price-lists", perm: "price-list.read", feature: "wholesale", uses: "sells_goods", hideFor: ["gym", "salon"] },
  { icon: HandCoins, label: "Receivables", path: "/receivables", perm: "credit.ageing.read", uses: "credit_sales" },
  { icon: Award, label: "Loyalty", path: "/loyalty", perm: "loyalty.program.read", uses: ["sells_goods", "bookings", "memberships"], shop: true },
  { icon: Sparkles, label: "Promotions", path: "/promotions", perm: "product.tier-price.update", uses: "sells_goods", shop: true },
  { icon: HeartHandshake, label: "Customer relationships", path: "/crm", perm: ["crm.follow-up.read", "crm.template.read"], feature: "crm" },
  // Trading with other businesses on Eaze (was its own one-item section).
  { icon: Handshake, label: "Connections", path: "/connections", perm: "connection.read", only: [...SHELF_RETAIL, "restaurant"] },
];

const ADMIN_STOCK: NavItem[] = [
  { icon: Package, label: "Products", path: "/products", termKey: "productsPageTitle", perm: "product.read" },
  { icon: AlertTriangle, label: "Low Stock", path: "/low-stock", termKey: "lowStockLabel", perm: "product.read", uses: "keeps_stock" },
  { icon: PackageSearch, label: "Reorder", path: "/reorder", perm: "purchase-order.create", only: [...SHELF_RETAIL, "restaurant"], feature: "purchasing", uses: "keeps_stock" },
  { icon: PackagePlus, label: "Receive stock", path: "/receive-stock", perm: "product.stock.add", uses: "keeps_stock" },
  { icon: ClipboardCheck, label: "Stock take", path: "/stock-take", perm: "stock-count.read", uses: "keeps_stock" },
  { icon: Tag, label: "Shelf labels", path: "/labels", perm: "product.read", uses: "keeps_stock", shop: true },
  { icon: ShoppingCart, label: "Purchasing", path: "/purchasing", perm: "purchase-order.read", only: [...SHELF_RETAIL, "restaurant", "construction"], feature: "purchasing", uses: "keeps_stock" },
  { icon: CalendarClock, label: "Stock expiry", path: "/stock-expiry", perm: "stock-expiry.report.read", only: ["retail", "restaurant", "pharmacy", "wholesale", "other"], uses: "keeps_stock" },
  // 12b: batches (where each is, holds, quality) and recalls.
  { icon: Layers, label: "Batches", path: "/batches", perm: "batch.read", uses: "keeps_stock", lots: true },
  { icon: ShieldAlert, label: "Recalls", path: "/recalls", perm: "batch.read", uses: "keeps_stock", lots: true },
];

// Distribution: getting goods to customers (switch on per business).
const ADMIN_DISTRIBUTION: NavItem[] = [
  { icon: Truck, label: "Dispatch", path: "/dispatch", perm: "dispatch.read", feature: "wholesale", uses: "distribution" },
  { icon: Radar, label: "Live map", path: "/distribution/live", perm: ["dispatch.read", "van.read"], feature: "wholesale", uses: "distribution" },
  { icon: IdCard, label: "Drivers", path: "/distribution/drivers", perm: ["driver.manage", "dispatch.read", "van.read"], feature: "wholesale", uses: "distribution" },
  { icon: History, label: "Run history", path: "/distribution/history", perm: ["dispatch.read", "van.read"], feature: "wholesale", uses: "distribution" },
  { icon: History, label: "My runs", path: "/my/runs", perm: ["dispatch.deliver", "van.sell"], hideIfPerm: "dispatch.read", feature: "wholesale", uses: "distribution" },
  { icon: Navigation, label: "My deliveries", path: "/deliveries", perm: "dispatch.deliver", hideIfPerm: "dispatch.read", feature: "wholesale", uses: "distribution" },
  { icon: Truck, label: "Vans", path: "/vans", perm: "van.read", feature: "wholesale", uses: "vans" },
  { icon: ShoppingBag, label: "My van", path: "/van", perm: "van.sell", hideIfPerm: "van.read", feature: "wholesale", uses: "vans" },
  { icon: MapPinned, label: "Routes & vehicles", path: "/distribution/routes", perm: "route.read", feature: "wholesale", uses: "distribution" },
];
// Reports that belong to distribution but read like reports.
const DISTRIBUTION_REPORTS: NavItem[] = [
  { icon: BarChart3, label: "Sales by territory", path: "/distribution/report", perm: "route.read", feature: "wholesale", uses: "distribution" },
];

// Make: turning raw materials into products (switch on per business).
const ADMIN_MAKE: NavItem[] = [
  { icon: Factory, label: "Recipes & production", path: "/manufacturing", perm: "bom.read", uses: "manufacturing" },
  { icon: Weight, label: "Weighbridge", path: "/weighbridge", perm: ["weighbridge.read", "weighbridge.weigh", "weighbridge.manage"], uses: "weighbridge" },
];

const ADMIN_MONEY: NavItem[] = [
  { icon: BadgeJapaneseYen, label: "Expenditures", path: "/expenditures", perm: "expenditure.read" },
  { icon: ClipboardCheck, label: "Day close", path: "/admin/shifts", perm: "shift.read.all", hideFor: ["construction"], businessOnly: true },
];
// The accountant's tools — folded away by default.
// 13f: Books (the overview and health check) leads; the tools follow.
const ADMIN_ACCOUNTS: NavItem[] = [
  { icon: Scale, label: "Books", path: "/books", perm: ["report.pnl.read", "report.balance-sheet.read", "report.trial-balance.read", "journal.manual.read"] },
  { icon: ListTree, label: "Chart of accounts", path: "/books/accounts", perm: ["report.trial-balance.read", "journal.manual.read", "report.balance-sheet.read"] },
  { icon: BookOpen, label: "Journals", path: "/books/journals", perm: "journal.manual.read" },
  { icon: Landmark, label: "Bank reconciliation", path: "/books/bank", perm: "bank.reconcile.read" },
  { icon: Warehouse, label: "Fixed assets", path: "/books/assets", perm: "fixed-asset.read" },
  { icon: Receipt, label: "Tax", path: "/books/tax", perm: ["report.tax.read", "tax.manage"] },
  { icon: Percent, label: "Tax rates", path: "/books/tax-rates", perm: "tax.manage" },
];

const ADMIN_INSIGHTS: NavItem[] = [
  {
    icon: FileBarChart,
    label: "Report Center",
    path: "/reports",
    perm: ["report.pnl.read", "report.sales.read", "report.cash-flow.read", "report.trial-balance.read", "report.tax.read"],
  },
];
// 12e: jobs / work orders (businesses that switch on "We do jobs for customers").
const ADMIN_JOBS: NavItem[] = [
  { icon: Wrench, label: "Jobs", path: "/jobs", perm: ["job.read", "job.read.own", "job.manage"], uses: "service_jobs" },
  { icon: CalendarRange, label: "Board", path: "/jobs/board", perm: ["job.read", "job.read.own", "job.manage"], uses: "service_jobs" },
  { icon: Timer, label: "Time & expenses", path: "/jobs/approvals", perm: "job.approve", uses: "service_jobs" },
  { icon: FileSignature, label: "Service contracts", path: "/service-contracts", perm: ["contract.manage", "job.read"], uses: "service_jobs" },
];
// 12d: pharmacies — dispensing, patients, the controlled register.
const ADMIN_PHARMACY: NavItem[] = [
  { icon: Pill, label: "Dispensing", path: "/dispensing", perm: "dispense.read", only: ["pharmacy"], dispensing: true },
  { icon: Contact, label: "Patients", path: "/patients", perm: "patient.read", only: ["pharmacy"], dispensing: true },
  { icon: BookLock, label: "Controlled register", path: "/controlled-register", perm: "controlled.read", only: ["pharmacy"], dispensing: true },
];
// 12c: one business's branches and its exceptions (business mode only).
const ADMIN_BRANCH_INSIGHTS: NavItem[] = [
  { icon: Building2, label: "Branch performance", path: "/branch-scorecard", perm: ["report.branch.read", "report.branch.read.own"] },
  { icon: AlertTriangle, label: "Exceptions", path: "/exceptions", perm: ["exception.read", "exception.manage"] },
];

const ADMIN_PEOPLE: NavItem[] = [
  { icon: Users, label: "Employees", path: "/hr/employees", perm: "employee.read", feature: "hr" },
  { icon: CalendarDays, label: "Leave", path: "/hr/leave", perm: ["leave.request.read.all", "leave.request.read.team"], feature: "hr" },
  { icon: Banknote, label: "Payroll", path: "/payroll", perm: "payroll.run.read", feature: "payroll" },
  { icon: UserRound, label: "My leave & pay", path: "/my/leave", perm: "employee.read.own", hideIfPerm: "employee.read" },
  // Team chat lives in the header (with its unread count).
];

const ADMIN_ADMIN: NavItem[] = [
  { icon: UserCog, label: "Team & access", path: "/team", perm: "access.read" },
  { icon: Building2, label: "Locations", path: "/settings/locations", perm: "location.read.tree" },
  { icon: ShieldCheck, label: "Audit log", path: "/audit-log", perm: "audit-log.read" },
  { icon: History, label: "Activity", path: "/action-history", perm: ["activity.read.all", "activity.read.own"] },
  { icon: Settings, label: "Settings", path: "/settings" },
];

// ── Industry menus ───────────────────────────────────────────────────
// A builder isn't a shop: the menu keeps only what a construction or real
// estate business uses, under its own names. Paths not listed are hidden.
interface IndustryMenu {
  keep: Set<string>;
  headers?: Record<string, string>;
  labels?: Record<string, string>;
  /** Till screens (by surface key) that make sense for this industry. */
  till?: Set<string>;
  /** The phone bottom bar's tabs, in order (first four that the person can open). */
  primary?: string[];
}
const INDUSTRY_MENUS: Partial<Record<IndustryId, IndustryMenu>> = {
  construction: {
    keep: new Set([
      "/dashboard",
      // Projects & clients
      "/projects", "/collections", "/customers", "/crm", "/messaging",
      // Selling goods or materials (Settings → What this business does)
      "/orders", "/returns", "/quotations", "/sales-orders", "/price-lists", "/receivables", "/promotions", "/loyalty",
      // Deliveries and making products, when switched on
      "/dispatch", "/distribution/live", "/distribution/drivers", "/distribution/history", "/my/runs", "/deliveries", "/distribution/routes", "/distribution/report", "/manufacturing", "/weighbridge",
      // Materials
      "/products", "/low-stock", "/receive-stock", "/purchasing", "/stock-take", "/branches", "/stock-transfers",
      // Money, insights, people, admin
      "/expenditures", "/reports", "/branch-scorecard", "/exceptions",
      "/jobs", "/jobs/board", "/jobs/approvals", "/service-contracts",
      "/hr/employees", "/hr/leave", "/payroll", "/my/leave", "/team-chat", "/action-history",
      "/team", "/audit-log", "/settings/locations", "/settings",
      "/group", "/group/approvals", "/group/catalog", "/group/transfers",
      "/cashier/collect",
    ]),
    headers: { Sell: "Projects", Customers: "Clients", Stock: "Materials", Till: "Collect payments" },
    labels: { "/receive-stock": "Receive materials", "/stock-take": "Count materials", "/branches": "Sites", "/stock-transfers": "Move materials" },
    till: new Set(["construction.collect", "core.pos"]),
    primary: ["/dashboard", "/projects", "/cashier/collect", "/products", "/reports"],
  },
};

/** Phone bottom bar tabs when the industry doesn't set its own. */
// Phone tabs: the first four of these the person can open. Jobs come
// early where jobs are switched on (12e); elsewhere they're simply absent.
export const DEFAULT_PRIMARY = ["/dashboard", "/jobs", "/orders", "/jobs/board", "/products", "/reports"];
/** …and in All businesses mode. */
export const GROUP_PRIMARY = ["/group", "/expenditures", "/payroll", "/reports"];

const menuOrder = (m: IndustryMenu, path: string) => {
  const i = [...m.keep].indexOf(path);
  return i < 0 ? 999 : i;
};

// Enterprise-only — gated by isEnterprise at render time.
const ENTERPRISE_GROUP: NavItem[] = [
  { icon: LayersIcon, label: "Group view", path: "/group", visibleTo: TENANT_LEVEL_ROLES, perm: "report.group.read" },
  { icon: ShieldCheck, label: "Approvals", path: "/group/approvals", visibleTo: ["tenant_owner", "super_admin", "business_admin"], perm: "group.approval.read" },
  { icon: Boxes, label: "Catalog", path: "/group/catalog", visibleTo: ["tenant_owner", "super_admin", "business_admin"], perm: "group.product.read" },
  { icon: ArrowRightLeft, label: "Transfers", path: "/group/transfers", visibleTo: ["tenant_owner", "super_admin", "business_admin"], perm: "group.transfer.read" },
];

/** In a business: the way back to the group's own menu (owners with several businesses). */
const ALL_BUSINESSES_ITEM: NavItem = { icon: LayersIcon, label: "All businesses", path: "/group", allBusinesses: true };

interface SidebarItemProps {
  item: NavItem;
  label: string;
  active: boolean;
  collapsed: boolean;
  badge?: number;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  item,
  label,
  active,
  collapsed,
  badge,
}) => {
  const Icon = item.icon;
  const leaveBusiness = useBusinessStore((s) => s.setActiveBusinessSlug);
  const hasBadge = (badge ?? 0) > 0;
  const to = item.locked ? `/settings?tab=subscription&need=${encodeURIComponent(item.feature || "")}` : item.path;
  const badgeText = (badge ?? 0) > 9 ? "9+" : String(badge ?? 0);
  const content = (
    <Link
      to={to}
      onClick={item.allBusinesses ? () => leaveBusiness(null) : undefined}
      aria-label={item.locked ? `${label} — not in your plan` : undefined}
      className={cn(
        "group relative flex items-center gap-3 rounded-md transition-all duration-150",
        "outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
        collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5",
        !active && !item.locked && "text-sidebar-foreground hover:bg-white/5 hover:text-white",
        !active && item.locked && "text-sidebar-foreground/45 hover:bg-white/5 hover:text-sidebar-foreground",
        active && "bg-sidebar-accent text-white font-medium",
      )}
    >
      {active && (
        <span className="absolute left-0 top-1/2 h-6 w-[3px] -translate-y-1/2 rounded-r bg-primary" />
      )}
      <span className="relative shrink-0">
        <Icon
          size={18}
          className={cn(
            "transition-colors",
            active
              ? "text-white"
              : "text-sidebar-foreground group-hover:text-white",
          )}
        />
        {collapsed && hasBadge && (
          <span
            className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary ring-2 ring-sidebar"
            aria-hidden="true"
          />
        )}
      </span>
      {!collapsed && <span className="truncate text-sm">{label}</span>}
      {!collapsed && item.locked && <Lock size={13} className="ml-auto shrink-0 opacity-70" aria-hidden />}
      {!collapsed && hasBadge && (
        <span
          className="ml-auto inline-flex items-center justify-center rounded-full bg-primary px-1.5 min-w-[18px] h-[18px] text-[10px] font-semibold text-white leading-none tabular-nums"
          aria-label={`${badge} unread`}
        >
          {badgeText}
        </span>
      )}
    </Link>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="right" className="ml-1">
          {item.locked ? `${label} · not in your plan` : hasBadge ? `${label} · ${badgeText}` : label}
        </TooltipContent>
      </Tooltip>
    );
  }
  return content;
};

interface SectionHeaderProps {
  label: string;
  collapsed: boolean;
  open: boolean;
  onToggle: () => void;
  controls: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ label, collapsed, open, onToggle, controls }) => {
  if (collapsed) {
    return (
      <div className="my-2 mx-2 h-px bg-sidebar-border" aria-hidden="true" />
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      aria-controls={controls}
      className="group mt-2 flex w-full items-center justify-between rounded-md px-3 pt-2 pb-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="text-[10px] font-semibold uppercase tracking-wider text-sidebar-foreground/60 group-hover:text-sidebar-foreground">
        {label}
      </span>
      <ChevronDown size={13} className={cn("text-sidebar-foreground/40 transition-transform", !open && "-rotate-90")} aria-hidden />
    </button>
  );
};

// Which sections someone folded away — remembered on this device only.
const FOLD_KEY = "eaze.sidebar.folded";
/** Pages about the people you sell to — their own section, apart from selling. */
const CUSTOMER_PATHS = new Set(["/customers", "/clients", "/crm", "/loyalty", "/connections", "/messaging"]);

/** Folded until someone opens them: tools most people visit rarely. */
const DEFAULT_FOLDED = ["Accounts", "Admin"];
function readFolded(): Set<string> {
  try {
    const raw = localStorage.getItem(FOLD_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : DEFAULT_FOLDED);
  } catch {
    return new Set(DEFAULT_FOLDED);
  }
}
function writeFolded(s: Set<string>) {
  try {
    localStorage.setItem(FOLD_KEY, JSON.stringify([...s]));
  } catch {
    /* private mode — folding just won't be remembered */
  }
}

function filterByRole(items: NavItem[], role: UserRole | undefined): NavItem[] {
  if (!role) return [];
  return items.filter((it) => !it.visibleTo || it.visibleTo.includes(role));
}

interface SidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

/**
 * The menu model (sections, items, labels, what's active) — shared by the
 * sidebar and the phone bottom bar so both follow the same industry menus,
 * permissions and plan.
 */
export function useNavModel() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);

  // Active business industry — supplies terminology AND drives which registry
  // surfaces appear (e.g. salon → schedule surface).
  const { t, industry } = useT();

  const chatUnread = useChatUnread();
  const { can } = usePermissions();
  const chatOnPlan = useTeamChatAvailable();

  // Their level where they're working now (a cashier at one branch can
  // manage another). Staff use the back office; what they see follows
  // their permissions.
  const role = useEffectiveRole() || user?.role;
  const isAdmin = role ? isAnyAdmin(role) || role === "staff" : false;
  const showCashierNav = role ? isCashier(role) : false;

  // ── Enterprise detection (carried from v6) ───────────────────────
  const { data: myBusinesses, isLoading: businessesLoading } =
    useMyBusinesses();

  // The plan decides (Enterprise, or the extra-business add-on unlocks group
  // tools). The stored plan name covers the first paint before it loads.
  const ent = useEntitlements();
  const activeSlug = useBusinessStore((s) => s.activeBusinessSlug);
  // What the open business uses (Settings → What this business does).
  const activeBiz = (myBusinesses as { slug?: string; segment?: string | null; features?: Partial<Record<BusinessFeatureKey, boolean>> }[] | undefined)?.find((b) => b.slug === activeSlug);
  // What kind of business this is (12a segment) — a pharma wholesaler
  // doesn't dispense, a cement plant doesn't print shelf labels.
  const segment = activeBiz?.segment || null;
  // Any one of the switches is enough. Unknown yet (loading) → show.
  const usesFeature = (k: BusinessFeatureKey | BusinessFeatureKey[]) => !activeBiz?.features || (Array.isArray(k) ? k : [k]).some((x) => activeBiz.features?.[x] !== false);
  const needs = (it: NavItem) => it.uses ?? PATH_USES[it.path];
  const switchedOn = (path?: string) => !path || !PATH_USES[path] || groupNav || usesFeature(PATH_USES[path]);
  const isOwner = role === "tenant_owner" || role === "super_admin";
  const planIsEnterprise =
    (ent.data?.planName || (user?.company?.subscription?.plan as string | undefined) || "").toLowerCase() === "enterprise";
  const groupTools = ent.data ? hasModule(ent.data, "multiBusiness") : planIsEnterprise;
  // Every business on the plan is in use → "Add business" leads to the add-on.
  const bizFull = atLimit(ent.data, "businesses");

  const lastEnterpriseRef = useRef<boolean | null>(null);

  const isEnterprise = useMemo(() => {
    if (planIsEnterprise) {
      lastEnterpriseRef.current = true;
      return true;
    }
    // Group tools only when the plan has them AND there is more than one business.
    const listLoaded = Array.isArray(myBusinesses);
    if (listLoaded) {
      const resolved = groupTools && (myBusinesses as unknown[]).length > 1;
      lastEnterpriseRef.current = resolved;
      return resolved;
    }
    return lastEnterpriseRef.current ?? false;
  }, [planIsEnterprise, groupTools, myBusinesses, businessesLoading]);

  const enterpriseGroupForRole = useMemo(
    () => (isEnterprise ? filterByRole(ENTERPRISE_GROUP, user?.role) : []),
    [isEnterprise, user?.role],
  );

  // ── Registry-driven nav composition ──────────────────────────────
  // One label resolver, shared by cashier + admin-injected surfaces. The
  // schedule surface's label is a NESTED term (terms.schedule.title) that the
  // flat t(termKey) can't reach, so special-case it.
  const labelForSurface = (s: SurfaceDef): string => {
    if (s.key === "core.schedule") {
      return industry.terms.schedule?.title ?? s.label;
    }
    return s.termKey ? t(s.termKey) : s.label;
  };

  const surfaceToNavItem = (s: SurfaceDef): NavItem => ({
    icon: s.icon,
    label: labelForSurface(s),
    path: s.path!,
    badgeKey: s.badgeKey,
    perm: s.perm,
  });

  // Cashier nav — fully registry-driven, grouped by surface.section.
  // The till's screens — for cashiers as their whole menu, and for anyone
  // else who sells (staff, managers) as a "Till" group in the back office.
  const canSell = can("order.create");
  const cashierSurfaces = showCashierNav || (isAdmin && canSell)
    ? getSurfaces({
        industry: industry.id,
        app: "cashier",
        role: "cashier" as Role,
        includePlanned: false,
        includeSheets: false,
      })
    : [];
  // Back office already has Products, Stock take, Expenditures, Customers,
  // Team chat, Account — the till group adds only what's the till's own.
  // Managers get the way in; the till has its own sales / returns / shift
  // screens, so "My sales" etc. aren't repeated here.
  const TILL_FOR_BACK_OFFICE = new Set(["core.pos", "gym.checkin", "construction.collect", "restaurant.tables"]);
  // In All businesses mode the menu is the group's, not one industry's.
  const groupNav = !!user && (user.role === "tenant_owner" || user.role === "super_admin") && !activeSlug && Array.isArray(myBusinesses) && myBusinesses.length > 1;
  const menu = groupNav ? undefined : INDUSTRY_MENUS[industry.id as IndustryId];
  const tillAllowed = (key: string) => (menu?.till ? menu.till.has(key) : TILL_FOR_BACK_OFFICE.has(key));
  const tillItems = isAdmin && canSell
    ? cashierSurfaces.filter((s) => tillAllowed(s.key) && s.path && (!s.perm || can(s.perm)) && switchedOn(s.path)).map((s) => {
        const item = surfaceToNavItem(s);
        return s.key === "core.pos" ? { ...item, label: "Open the till" } : s.key === "core.sales" ? { ...item, label: "My sales" } : s.key === "core.returns" ? { ...item, label: "My returns" } : s.key === "core.shift" ? { ...item, label: "My shift" } : item;
      })
    : [];
  const cashierOps = cashierSurfaces
    .filter((s) => (!s.perm || can(s.perm)) && switchedOn(s.path))
    .filter((s) => (s.section ?? "operations") === "operations")
    .map(surfaceToNavItem);
  const cashierAcct = cashierSurfaces
    .filter((s) => !s.perm || can(s.perm))
    .filter((s) => s.section === "account")
    .filter((s) => chatOnPlan || s.badgeKey !== "chatUnread")
    .map(surfaceToNavItem);

  // Admin nav — declarative chrome + injected LIVE industry/module surfaces
  // into Workspace (e.g. a salon admin gains "Appointments").
  const adminInjected = isAdmin
    ? getSurfaces({
        industry: industry.id,
        app: "admin",
        role: (role === "staff" ? "business_admin" : role ?? "business_admin") as Role,
        includePlanned: false,
        includeSheets: false,
      }).map(surfaceToNavItem)
    : [];

  // Branches and transfers are where branch and warehouse work happens, so
  // they're always in the menu for people allowed to use them — even with one
  // branch (that's when people set up the second).
  const branchNavItems: NavItem[] = [
    { icon: Building2, label: "Branches & warehouses", path: "/branches", perm: "branch.overview.read" },
    { icon: ArrowRightLeft, label: "Stock transfers", path: "/stock-transfers", perm: "location.read", uses: "keeps_stock" },
  ];

  // What kind of business is open.
  const ind = industry.id as IndustryId;
  const sellsInBulk = ind === "wholesale" || ind === "manufacturing" || (!!segment && BULK_SEGMENTS.has(segment));
  const shopFloor = SHOP_INDUSTRIES.includes(ind) && !sellsInBulk;
  const dispenses = ind === "pharmacy" && (!segment || segment === "retail_pharmacy");
  const kindFits = (it: NavItem) =>
    groupNav
      ? !it.businessOnly
      : (!it.shop || shopFloor) && (!it.lots || LOT_INDUSTRIES.includes(ind)) && (!it.dispensing || dispenses);

  // Industry + permission filter for declarative items.
  const fitsHere = (it: NavItem) =>
    kindFits(it) &&
    (!needs(it) || groupNav || usesFeature(needs(it)!)) &&
    (!menu || menu.keep.has(it.path)) &&
    (groupNav || !it.only || it.only.includes(industry.id as IndustryId)) &&
    (groupNav || !it.hideFor?.includes(industry.id as IndustryId)) &&
    (chatOnPlan || it.badgeKey !== "chatUnread");
  const onPlan = (it: NavItem) => !it.feature || hasModule(ent.data, it.feature);
  const allowed = (it: NavItem) =>
    fitsHere(it) &&
    onPlan(it) &&
    (!it.perm || can(it.perm)) &&
    (!it.hideIfPerm || !can(it.hideIfPerm));
  // Not in the plan: the owner sees it locked (a way to find out what an
  // upgrade adds); nobody else sees it.
  const keep = (items: NavItem[]) =>
    items.flatMap((it) => (allowed(it) ? [it] : isOwner && fitsHere(it) && !onPlan(it) && !it.hideIfPerm ? [{ ...it, locked: true }] : []));

  // Words that only fit one industry (a repair shop has technicians, not stylists).
  const industryLabel = (it: NavItem): NavItem =>
    it.path === "/stylists" && ind !== "salon" ? { ...it, label: usesFeature("service_jobs") ? "Technicians" : "Staff" } : it;

  // Selling and the industry's own screens, before they're split into
  // Sell / Customers / People below.
  const sellItems = [...keep(ADMIN_SELL), ...keep(adminInjected).map((it) => industryLabel(it))];
  const jobsOn = keep(ADMIN_JOBS).some((it) => !it.locked);
  // A builder's sites are day-to-day work; elsewhere branches are set-up.
  const sitesAreWork = ind === "construction";

  // All businesses mode (owner, several businesses, none open): Money,
  // Insights, People and Admin work from here; selling and stock open a
  // business first.
  const allBusinessesMode = groupNav;
  const GROUP_HOME: NavItem[] = [
    { icon: LayersIcon, label: "Group overview", path: "/group", perm: "report.group.read" },
    { icon: Building2, label: "Branches", path: "/group/branches", perm: "report.group.read" },
    { icon: Building2, label: "Open a business", path: "/portfolio" },
  ];

  const navGroups: { header: string; items: NavItem[] }[] = (allBusinessesMode
    ? [
        { header: "Home", items: keep(GROUP_HOME) },
        ...(groupTools ? [{ header: "Group", items: keep(ENTERPRISE_GROUP.filter((it) => it.path !== "/group")) }] : []),
        { header: "Money", items: keep(ADMIN_MONEY) },
        { header: "Accounts", items: keep(ADMIN_ACCOUNTS) },
        { header: "Reports", items: keep(ADMIN_INSIGHTS) },
        { header: "People", items: keep(ADMIN_PEOPLE) },
        { header: "Admin", items: keep(ADMIN_ADMIN) },
      ]
    : isAdmin
    ? [
        // One business open: only its own work. Group tools live in All
        // businesses — one item leads back there.
        { header: "Home", items: [...keep(ADMIN_HOME), ...(enterpriseGroupForRole.length > 0 ? [ALL_BUSINESSES_ITEM] : [])] },
        ...(tillItems.length ? [{ header: "Till", items: tillItems }] : []),
        // The industry's own work comes first.
        { header: "Pharmacy", items: keep(ADMIN_PHARMACY) },
        // Bookings sit with the jobs they turn into, where jobs are on.
        { header: "Jobs", items: [...keep(ADMIN_JOBS), ...(jobsOn ? sellItems.filter((it) => it.path === "/cashier/schedule") : [])] },
        { header: "Sell", items: sellItems.filter((it) => !CUSTOMER_PATHS.has(it.path) && it.path !== "/stylists" && !(jobsOn && it.path === "/cashier/schedule")) },
        { header: "Customers", items: sellItems.filter((it) => CUSTOMER_PATHS.has(it.path)) },
        { header: "Distribution", items: keep(ADMIN_DISTRIBUTION) },
        { header: "Make", items: keep(ADMIN_MAKE) },
        { header: usesFeature("keeps_stock") ? "Stock" : "Catalogue", items: [...keep(ADMIN_STOCK), ...keep(branchNavItems.filter((it) => sitesAreWork || it.path !== "/branches"))] },
        { header: "Money", items: keep(ADMIN_MONEY) },
        { header: "Accounts", items: keep(ADMIN_ACCOUNTS) },
        { header: "Reports", items: [...keep(ADMIN_INSIGHTS), ...keep(ADMIN_BRANCH_INSIGHTS), ...keep(DISTRIBUTION_REPORTS)] },
        { header: "People", items: [...keep(ADMIN_PEOPLE), ...sellItems.filter((it) => it.path === "/stylists")] },
        // Set-up: branches and warehouses sit with locations and settings.
        { header: "Admin", items: [...keep(branchNavItems.filter((it) => !sitesAreWork && it.path === "/branches")), ...keep(ADMIN_ADMIN)] },
      ]
    : showCashierNav
      ? [
          { header: "Operations", items: cashierOps },
          { header: "Account", items: cashierAcct },
        ]
      : []
  )
    .map((g) => ({
      ...g,
      header: menu?.headers?.[g.header] ?? g.header,
      items: g.items
        .map((it) => (menu?.labels?.[it.path] ? { ...it, label: menu.labels[it.path], termKey: undefined } : it))
        // An industry menu also sets the order (the work first).
        .sort((x, y) => (menu ? menuOrder(menu, x.path) - menuOrder(menu, y.path) : 0)),
    }))
    // Each page once: the first section that lists it keeps it (the
    // industry's own sections come first), and two items with the same name
    // in one menu are one too many.
    .reduce<{ header: string; items: NavItem[] }[]>((acc, g) => {
      const seenPaths = new Set(acc.flatMap((x) => x.items.map((it) => it.path)));
      const seenLabels = new Set(acc.flatMap((x) => x.items.map((it) => (it.termKey ? t(it.termKey) : it.label).toLowerCase())));
      const items: NavItem[] = [];
      for (const it of g.items) {
        const name = (it.termKey ? t(it.termKey) : it.label).toLowerCase();
        if (seenPaths.has(it.path) || seenLabels.has(name)) continue;
        seenPaths.add(it.path);
        seenLabels.add(name);
        items.push(it);
      }
      acc.push({ ...g, items });
      return acc;
    }, [])
    .filter((g) => g.items.length > 0);

  // Folded sections (the one holding the current page always opens).
  const [folded, setFolded] = useState<Set<string>>(() => readFolded());
  const toggleSection = (header: string) => {
    setFolded((prev) => {
      const next = new Set(prev);
      if (next.has(header)) next.delete(header);
      else next.add(header);
      writeFolded(next);
      return next;
    });
  };

  // Render-time label: declarative items may carry a termKey; surface-derived
  // items already have a resolved label and no termKey.
  const labelFor = (item: NavItem): string =>
    item.termKey ? t(item.termKey) : item.label;

  const isItemActive = (path: string) => {
    if (path === "/dashboard" || path === "/cashier" || path === "/group" || path === "/books") {
      return location.pathname === path;
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + "/")
    );
  };

  return {
    user,
    t,
    industry,
    chatUnread,
    isEnterprise,
    bizFull,
    navGroups,
    labelFor,
    isItemActive,
    groupNav,
    isAdmin,
    showCashierNav,
    menu,
    primaryPaths: groupNav ? GROUP_PRIMARY : menu?.primary ?? DEFAULT_PRIMARY,
    folded,
    toggleSection,
  };
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onToggle,
}) => {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const [addOpen, setAddOpen] = useState(false);
  const navigate = useNavigate();

  const {
    chatUnread,
    isEnterprise,
    bizFull,
    navGroups,
    labelFor,
    isItemActive,
    showCashierNav,
    folded,
    toggleSection,
  } = useNavModel();
  const addBusiness = () => (bizFull ? navigate("/settings?tab=subscription&need=business") : setAddOpen(true));

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <aside
        className={cn(
          "fixed left-0 top-0 z-30 h-screen flex-col",
          "bg-sidebar border-r border-sidebar-border",
          "transition-[width] duration-300 ease-in-out",
          collapsed ? "w-16" : "w-64",
          showCashierNav ? "hidden lg:flex" : "flex",
        )}
        aria-label="Main navigation"
      >
        {/* Brand and the fold button on one row; who's signed in is in the
            header's account menu, so the menu itself starts higher. */}
        <div
          className={cn(
            "flex h-14 shrink-0 items-center border-b border-sidebar-border sm:h-16",
            collapsed ? "flex-col justify-center gap-1 px-2" : "gap-3 px-3",
          )}
        >
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white p-1.5">
            <img src={Logo} alt="Eaze" className="h-full w-full object-contain" />
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-[15px] font-semibold text-white">Eaze</div>
              <div className="truncate text-[10px] uppercase tracking-wider text-sidebar-foreground/60">
                {isEnterprise ? "Enterprise" : "Operations suite"}
              </div>
            </div>
          )}
          {!collapsed && (
            <button
              type="button"
              onClick={onToggle}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sidebar-foreground/60 outline-none transition-colors hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-primary"
              aria-label="Collapse sidebar"
              title="Collapse"
            >
              <ChevronLeft size={16} />
            </button>
          )}
        </div>
        {collapsed && (
          <button
            type="button"
            onClick={onToggle}
            className="mx-2 mt-2 flex h-8 items-center justify-center rounded-md text-sidebar-foreground/60 outline-none transition-colors hover:bg-white/5 hover:text-white focus-visible:ring-2 focus-visible:ring-primary"
            aria-label="Expand sidebar"
            title="Expand"
          >
            <ChevronRight size={16} />
          </button>
        )}

        <nav
          className="flex-1 overflow-y-auto overflow-x-hidden px-2 pb-3 [scrollbar-width:thin] [scrollbar-color:rgb(255_255_255_/_0.1)_transparent]"
          aria-label="Primary"
        >
          {navGroups.map((group, idx) => {
            const hasActive = group.items.some((it) => !it.locked && isItemActive(it.path));
            const open = collapsed || hasActive || !folded.has(group.header);
            const listId = `nav-${group.header.toLowerCase().replace(/\W+/g, "-")}`;
            return (
            <div key={group.header}>
              {(idx > 0 || !collapsed) && (
                <SectionHeader label={group.header} collapsed={collapsed} open={open} onToggle={() => toggleSection(group.header)} controls={listId} />
              )}
              <div id={listId} className="space-y-0.5" hidden={!open}>
                {group.items.map((item) => (
                  <SidebarItem
                    key={`${item.path}${item.locked ? ":locked" : ""}`}
                    item={item}
                    label={labelFor(item)}
                    active={isItemActive(item.path)}
                    collapsed={collapsed}
                    badge={
                      item.badgeKey === "chatUnread" ? chatUnread : undefined
                    }
                  />
                ))}
              </div>
            </div>
            );
          })}

          {user?.role === "tenant_owner" && (
            <>
              <div className="mt-3 px-1">
                {collapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={addBusiness}
                        aria-label="Add business"
                        className={cn(
                          "flex w-full items-center justify-center rounded-lg py-2.5",
                          "border border-dashed border-primary/40 text-primary",
                          "hover:border-primary hover:bg-primary/10 transition-colors",
                          "outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
                        )}
                      >
                        <Plus size={18} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" className="ml-1">
                      Add business
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <button
                    type="button"
                    onClick={addBusiness}
                    className={cn(
                      "group flex w-full items-center gap-3 rounded-lg px-3 py-2.5",
                      "border border-dashed border-primary/40 text-left",
                      "hover:border-primary hover:bg-primary/10 transition-colors",
                      "outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
                    )}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/15 text-primary group-hover:bg-primary/25 transition-colors">
                      <Plus size={15} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-white">
                        Add business
                      </span>
                      <span className="block truncate text-[11px] text-sidebar-foreground/60">
                        {bizFull ? "Add one to your plan first" : "Its own books, stock and team"}
                      </span>
                    </span>
                    <Building2
                      size={15}
                      className="shrink-0 text-sidebar-foreground/40 group-hover:text-primary transition-colors"
                    />
                  </button>
                )}
              </div>
              <AddBusinessDialog open={addOpen} onOpenChange={setAddOpen} />
            </>
          )}
        </nav>

        <div className="border-t border-sidebar-border p-2">
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={handleLogout}
                  className={cn(
                    "flex w-full items-center justify-center rounded-md py-2.5",
                    "text-sidebar-foreground/70 hover:bg-destructive/10 hover:text-destructive",
                    "transition-colors duration-150",
                    "outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
                  )}
                  aria-label="Logout"
                >
                  <LogOut size={18} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="ml-1">
                Logout
              </TooltipContent>
            </Tooltip>
          ) : (
            <button
              onClick={handleLogout}
              className={cn(
                "flex w-full items-center gap-3 rounded-md px-3 py-2.5",
                "text-sidebar-foreground/70 hover:bg-destructive/10 hover:text-destructive",
                "transition-colors duration-150 text-sm",
                "outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar",
              )}
            >
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          )}
        </div>

        {!collapsed && (
          <div className="border-t border-sidebar-border px-4 py-3">
            <div className="text-[10px] text-sidebar-foreground/40 uppercase tracking-wider">
              © 2026 · Eaze
            </div>
          </div>
        )}
      </aside>
    </TooltipProvider>
  );
};
